// src/pages/Memora_documentacao.js
// Documentação da Memor.IA: os pilares de cada tipo de análise e o que
// define uma boa (e uma má) qualidade de entrega em cada setor.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/memora_op.css';
import CustoCopiloto from '../components/CustoCopiloto';
import MemoraAnimacao from '../components/MemoraAnimacao';

const TIPOS = {
  servico: {
    label: 'Serviço / Consultivo',
    setores: 'Contabilidade, BPO Financeiro, Fiscal, DP',
    cor: '#d610b5',
    icone: 'fa-handshake',
    pergunta: 'O operador atuou de forma consultiva, indo além do que foi pedido, ou só executou a demanda pontual?',
    competencias: [
      { nome: 'Diagnóstico e Descoberta', desc: 'Faz perguntas abertas, investiga a causa raiz das dores e busca entender o negócio do cliente além da demanda pontual.' },
      { nome: 'Proatividade e Antecipação', desc: 'Traz recomendações e alertas não solicitados: prazos, multas, caixa, compliance, planejamento tributário, créditos a recuperar e projeções.' },
      { nome: 'Domínio Técnico', desc: 'Responde com segurança e correção, sem enrolar nem prometer o que não sabe.' },
      { nome: 'Tradução de Valor', desc: 'Explica em linguagem de negócio, mostrando o impacto para o cliente — nada de tecniquês.' },
      { nome: 'Condução da Reunião', desc: 'Conduz com agenda, objetivo e ritmo, em vez de apenas reagir à pauta do cliente.' },
      { nome: 'Gestão de Objeções e Insatisfações', desc: 'Acolhe, investiga e trata reclamações sobre o NOSSO serviço. Reclamação sobre concorrente não é objeção — é oportunidade de expansão.' },
      { nome: 'Compromisso e Follow-through', desc: 'Fecha cada assunto com próximo passo claro: o quê, quem e quando.' },
      { nome: 'Orientação a Dados', desc: 'Conduz a conversa com números, indicadores, BI, relatórios e projeções — em vez de impressões e opiniões.' },
    ],
    boa: [
      'Conversa guiada por dados: BI, DRE, indicadores e projeções na tela',
      'Perguntas abertas que chegam à causa raiz das dores do cliente',
      'Recomendações e alertas que o cliente não pediu (tributário, caixa, prazos)',
      'Números traduzidos em decisão: "isso te economiza X", "isso evita a multa Y"',
      'Reunião termina com plano: cada assunto com dono e prazo',
    ],
    ma: [
      'Reunião vira despacho de pendências, sem condução',
      'Operador só responde o que foi perguntado — postura de tirador de pedido',
      'Tecniquês sem tradução: cliente sai sem entender o impacto',
      'Cliente menciona um problema e o operador só anota (dor ignorada)',
      'Nenhum número ou indicador citado na conversa inteira',
      'Termina sem próximos passos definidos',
    ],
    ancoras: [
      { faixa: '0–3', cor: '#dc3545', texto: 'Atendimento reativo, cliente insatisfeito ou dores ignoradas, sem próximos passos' },
      { faixa: '4–6', cor: '#fd7e14', texto: 'Executor competente: resolveu o que foi pedido, mas sem postura consultiva' },
      { faixa: '7–8', cor: '#0d6efd', texto: 'Postura consultiva presente: diagnóstico, recomendações e condução claras' },
      { faixa: '9–10', cor: '#198754', texto: 'Consultor estratégico: antecipou riscos, gerou valor além do escopo e o cliente reconheceu' },
    ],
    beneficios: [
      { icon: 'fa-door-open',        titulo: 'Preparação da próxima reunião', texto: 'A Memor.IA prepara sua entrada na próxima reunião com o cliente: resume o histórico, a dor principal, as pendências e compromissos abertos, as objeções que podem voltar, sugere como abrir a conversa e monta um checklist do que levar.' },
      { icon: 'fa-headphones',       titulo: 'Ouça, não precisa ler',       texto: 'Sem tempo de ler? A Memor.IA narra a preparação da sua próxima reunião em áudio — você ouve tudo a caminho da call, na velocidade que preferir (1x, 1.25x ou 1.5x).' },
      { icon: 'fa-heart-pulse',      titulo: 'Dores rastreadas',            texto: 'Cada dor mencionada pelo cliente fica registrada com contexto — nada se perde entre uma reunião e outra.' },
      { icon: 'fa-gem',              titulo: 'Expansão dentro da base',     texto: 'Oportunidades de upsell e cross-sell que aparecem no atendimento viram leads no funil de vendas.' },
      { icon: 'fa-triangle-exclamation', titulo: 'Insatisfação antecipada', texto: 'Reclamações e sinais de desgaste geram alerta antes de virarem cancelamento.' },
      { icon: 'fa-list-check',       titulo: 'Tarefas automáticas',         texto: 'Os próximos passos combinados viram tarefas no Google Tasks dos responsáveis, sem digitação.' },
      { icon: 'fa-chart-line',       titulo: 'Evolução consultiva',         texto: 'O radar mostra se cada operador está evoluindo de executor para consultor, reunião a reunião.' },
      { icon: 'fa-graduation-cap',   titulo: 'Treinamento sob medida',      texto: 'A Memor.IA gera treinamento personalizado com base nos pontos fracos reais de cada operador.' },
    ],
  },
  comercial: {
    label: 'Comercial / Vendas',
    setores: 'Time comercial e expansão de contas',
    cor: '#0d6efd',
    icone: 'fa-funnel-dollar',
    pergunta: 'O operador conduziu uma venda consultiva com avanço concreto, ou apresentou a solução e esperou pelo retorno do cliente?',
    competencias: [
      { nome: 'Descoberta de Dor', desc: 'Faz perguntas abertas e investiga a dor, o impacto e a urgência do problema do prospect antes de ofertar.' },
      { nome: 'Qualificação', desc: 'Identifica decisor, orçamento, prazo e aderência (fit) da solução ao cliente.' },
      { nome: 'Domínio do Produto', desc: 'Apresenta a solução com segurança e correção, sem enrolar.' },
      { nome: 'Apresentação do Produto', desc: 'Demo clara, estruturada e personalizada à realidade do prospect — sem se perder em funcionalidades irrelevantes.' },
      { nome: 'Apresentação de Valor', desc: 'Vende benefício e impacto (ROI, economia, risco evitado) em vez de só listar funcionalidades.' },
      { nome: 'Condução da Reunião', desc: 'Abre com agenda, controla o ritmo e o tempo, direciona a conversa.' },
      { nome: 'Gestão de Objeções', desc: 'Acolhe, investiga e responde objeções (preço, timing, concorrente) em vez de ser evasivo.' },
      { nome: 'Fechamento e Avanço', desc: 'Pede compromisso e sai da reunião com um avanço concreto: proposta, data, decisor envolvido.' },
    ],
    boa: [
      'Descoberta antes da demonstração: entende a dor antes de mostrar a solução',
      'Qualifica decisor, orçamento e prazo durante a conversa',
      'Demo personalizada com exemplos do contexto do prospect',
      'Conecta cada funcionalidade a um benefício ou número (ROI)',
      'Objeções respondidas na hora, sem fugir do assunto',
      'Sai com avanço concreto: proposta agendada, data marcada, decisor envolvido',
    ],
    ma: [
      'Apresentação sem descoberta: monólogo de funcionalidades',
      'Não identifica quem decide nem qual o orçamento',
      'Objeções ficam sem resposta ou são ignoradas',
      'Sinais de compra do prospect passam despercebidos',
      'Reunião termina sem pedido de compromisso ou próximo passo com data',
    ],
    ancoras: [
      { faixa: '0–3', cor: '#dc3545', texto: 'Prospect desinteressado ou reunião sem descoberta, sem tratamento de objeções e sem avanço' },
      { faixa: '4–6', cor: '#fd7e14', texto: 'Apresentação correta, mas venda passiva: pouca descoberta, sem pedido de avanço' },
      { faixa: '7–8', cor: '#0d6efd', texto: 'Venda consultiva: descoberta real, valor conectado à dor, objeções tratadas, avanço combinado' },
      { faixa: '9–10', cor: '#198754', texto: 'Reunião exemplar: prospect engajado, objeções resolvidas, avanço concreto rumo ao fechamento' },
    ],
    beneficios: [
      { icon: 'fa-shield-halved',   titulo: 'Objeções mapeadas',          texto: 'Cada objeção do cliente é registrada e categorizada (preço, prazo, concorrente...) — você enxerga o que mais trava as vendas.' },
      { icon: 'fa-funnel-dollar',   titulo: 'Funil automático',           texto: 'Cada lead entra no funil com etapa, interesse, produtos de interesse e chance de fechamento calculada.' },
      { icon: 'fa-chess',           titulo: 'Estratégia de fechamento',   texto: 'A Memor.IA monta a estratégia para fechar cada venda com base no histórico real de reuniões daquele cliente.' },
      { icon: 'fa-bell',            titulo: 'Follow-up garantido',        texto: 'Negociação parada ou sem próximos passos gera alerta — nenhum lead esfria em silêncio.' },
      { icon: 'fa-door-open',       titulo: 'Preparação de reunião',      texto: 'Antes da próxima call, a Memor.IA resume tudo do lead: dores, objeções abertas e o que retomar.' },
      { icon: 'fa-headphones',      titulo: 'Ouça, não precisa ler',      texto: 'A Memor.IA narra a preparação e a estratégia de fechamento em áudio — você ouve tudo a caminho da call, na velocidade que preferir (1x, 1.25x ou 1.5x).' },
      { icon: 'fa-chart-line',      titulo: 'Evolução do vendedor',       texto: 'O radar mostra onde cada vendedor é forte e onde perde vendas — da descoberta ao fechamento.' },
    ],
  },
  vision: {
    label: 'Reunião Vision',
    setores: 'Assessoria contábil com o Vision (BI contábil da Comece)',
    cor: '#0a8fa8',
    icone: 'fa-chart-pie',
    pergunta: 'O contador atuou como assessor de performance — interpretou os números, explicou causas e recomendou ações com o Vision — ou só leu o relatório?',
    competencias: [
      { nome: 'Visão Executiva', desc: 'Resume o cenário com números: receita subiu ou caiu, lucro ou prejuízo, margem, caixa.' },
      { nome: 'Destaque Positivo', desc: 'Aponta e valoriza pelo menos um ponto positivo com número (margem, custos, vendas, EBITDA, caixa).' },
      { nome: 'Principal Gargalo', desc: 'Identifica o indicador que mais preocupa, dimensiona ("folha = XX% da receita") e explica o porquê.' },
      { nome: 'Perguntas Estratégicas', desc: 'Provoca o gestor com até três perguntas (clientes de menor margem, contas que cresceram acima da receita, contratos a renegociar, caixa suporta o crescimento?).' },
      { nome: 'Impacto Potencial', desc: 'Quantifica "se isso melhorar, o que acontece?" — ex.: reduzir 2% do custo aumenta a margem sem vender mais.' },
      { nome: 'Recomendação', desc: 'Dá até três recomendações objetivas, ligadas ao gargalo — nada de conselho genérico.' },
      { nome: 'Demonstração do Vision', desc: 'Usa o Vision na conversa (indicadores, evolução, aberturas) e mostra ao cliente como explorar o BI para decidir.' },
      { nome: 'Próximos Passos', desc: 'Fecha com combinados claros (o quê, quem, quando), ex.: acompanhar o indicador X no Vision.' },
    ],
    boa: [
      'Cada indicador responde: o que aconteceu, por quê, qual o risco ou oportunidade e qual a ação',
      'Vision aberto na tela sustentando a conversa, com evolução e aberturas',
      'Gargalo dimensionado e com causa explicada',
      'Impacto quantificado e recomendações objetivas',
      'OBRIGATÓRIO: pergunta se o cliente tem alguma dificuldade em que o time de Inteligência de Dados pode ajudar',
      'Termina com próximos passos com dono e prazo',
    ],
    ma: [
      'Leitura de relatório: "a receita foi R$ X" sem interpretação',
      'Vision ausente ou só citado de passagem',
      'Cliente reage a um número e o contador não aprofunda',
      'Não pergunta sobre dificuldades que a Inteligência de Dados poderia resolver',
      'Termina sem recomendação nem próximos passos',
    ],
    ancoras: [
      { faixa: '0–3', cor: '#dc3545', texto: 'Leitura de relatório, Vision ausente, sem recomendação nem próximos passos' },
      { faixa: '4–6', cor: '#fd7e14', texto: 'Números com alguma explicação, mas sem gargalo dimensionado, impacto ou recomendação clara' },
      { faixa: '7–8', cor: '#0d6efd', texto: 'Visão executiva, gargalo com causa, recomendações objetivas e Vision sustentando a conversa' },
      { faixa: '9–10', cor: '#198754', texto: 'Framework completo: perguntas estratégicas, impacto quantificado e cliente engajado com o Vision' },
    ],
    beneficios: [
      { icon: 'fa-gauge-high',      titulo: 'Cenário do cliente',          texto: 'Cada reunião termina com a etiqueta do cenário do cliente — Baixo Risco, Atenção, Alerta ou Crítico — para priorizar a carteira.' },
      { icon: 'fa-magnifying-glass-chart', titulo: 'Assessoria medida',   texto: 'Você enxerga se o contador interpreta e recomenda ou só lê números — reunião a reunião.' },
      { icon: 'fa-chart-pie',       titulo: 'Adoção do Vision',            texto: 'Interesse e engajamento do cliente com o BI ficam registrados — munição para expandir o uso do Vision.' },
      { icon: 'fa-database',        titulo: 'Demanda para Inteligência de Dados', texto: 'A pergunta obrigatória transforma dificuldades do cliente em oportunidades para o time de Inteligência de Dados.' },
      { icon: 'fa-headset',         titulo: 'Copiloto ao vivo',            texto: 'Durante a reunião, o Copiloto sugere a próxima pergunta, lembra a pergunta obrigatória e marca o checklist do Vision.' },
    ],
  },
  reforma_tributaria: {
    label: 'Reforma Tributária',
    setores: 'Consultoria sobre IBS, CBS e Imposto Seletivo (EC 132/2023 e LC 214/2025)',
    cor: '#fd7e14',
    icone: 'fa-landmark',
    pergunta: 'O contador atuou como consultor — ligou a reforma ao negócio do cliente, apontou impactos e decisões e montou um plano — ou só explicou a lei?',
    competencias: [
      { nome: 'Diagnóstico Tributário', desc: 'Entende o perfil do cliente antes de opinar: regime, setor, vendas a empresas ou consumidor final, estados, compras e benefícios fiscais.' },
      { nome: 'Explicação da Reforma', desc: 'Explica o essencial (IBS, CBS, IS, o que substituem, créditos, destino, split payment) em linguagem de negócio.' },
      { nome: 'Cronograma', desc: 'Situa o cliente nas fases — 2026 teste, 2027 CBS integral, 2029–2032 transição, 2033 modelo completo — e diz o que muda para ele em cada uma.' },
      { nome: 'Impacto no Negócio', desc: 'Discute o efeito concreto em carga, preços, margem, créditos e caixa (split payment), comparando o cenário atual com o futuro.' },
      { nome: 'Decisões Estratégicas', desc: 'Leva as decisões do cliente: Simples ou regime regular, preços e contratos, fornecedores pelos créditos, fim de benefícios.' },
      { nome: 'Preparação Operacional', desc: 'Trata de sistema/ERP, novos campos da nota, cadastro NCM/NBS, obrigações acessórias e capacitação da equipe.' },
      { nome: 'Segurança Técnica', desc: 'Passa informação correta, não promete economia e deixa claro o que ainda depende de regulamentação.' },
      { nome: 'Plano de Ação', desc: 'Fecha com próximos passos claros: simulação de impacto, revisão de contratos, ajuste de cadastro — com dono e prazo.' },
    ],
    boa: [
      'Diagnóstico do negócio antes de falar da lei',
      'Reforma traduzida em impacto concreto: preço, margem, crédito e caixa',
      'Decisões apontadas e priorizadas pelo cronograma',
      'Preparação operacional (ERP, nota, cadastro) na pauta',
      'Deixa claro o que ainda é incerto na regulamentação',
      'Termina com plano de ação com dono e prazo',
    ],
    ma: [
      'Aula genérica sobre a lei, sem ligar à realidade do cliente',
      'Informação errada ou promessa de economia',
      'Dúvida do cliente sem aprofundamento',
      'Nenhuma decisão ou impacto discutido',
      'Termina sem próximos passos',
    ],
    ancoras: [
      { faixa: '0–3', cor: '#dc3545', texto: 'Aula genérica, sem diagnóstico, com informação errada ou sem próximos passos' },
      { faixa: '4–6', cor: '#fd7e14', texto: 'Explicação correta com alguma ligação ao cliente, mas sem impacto, decisões ou plano' },
      { faixa: '7–8', cor: '#0d6efd', texto: 'Diagnóstico, impacto discutido, decisões apontadas, preparação operacional e plano de ação' },
      { faixa: '9–10', cor: '#198754', texto: 'Consultoria exemplar: impacto quantificado, decisões priorizadas por fase e plano com dono e prazo' },
    ],
    beneficios: [
      { icon: 'fa-traffic-light',   titulo: 'Prontidão do cliente',        texto: 'Cada reunião termina com a etiqueta de prontidão — Preparado, Atenção, Exposto ou Crítico — para saber quem precisa de ajuda primeiro.' },
      { icon: 'fa-triangle-exclamation', titulo: 'Informação errada vira alerta', texto: 'Erro técnico dito ao cliente é registrado como risco alto — antes de virar problema.' },
      { icon: 'fa-gem',             titulo: 'Projetos de adequação',       texto: 'Interesse em simulação de impacto, revisão de contratos e adequação à reforma vira oportunidade no funil.' },
      { icon: 'fa-headset',         titulo: 'Copiloto ao vivo',            texto: 'Durante a reunião, o Copiloto lembra o cronograma, as decisões do cliente e alerta quando algo ainda depende de regulamentação.' },
    ],
  },
  gestao: {
    label: 'Gestão de Pessoas / 1:1',
    setores: 'Reuniões one-on-one entre gestor e liderado',
    cor: '#6610f2',
    icone: 'fa-people-arrows',
    pergunta: 'O gestor liderou de verdade — ouviu, desenvolveu e destravou — ou apenas cobrou status? E o liderado participou ativamente ou só assistiu?',
    competencias: [
      { nome: 'Gestor · Escuta Ativa', desc: 'Deixa o liderado falar mais do que ele, não interrompe e explora o que ouviu. A proporção de fala da reunião é a evidência.' },
      { nome: 'Gestor · Perguntas Poderosas', desc: 'Faz perguntas abertas sobre motivação, carreira e dificuldades — em vez de só cobrar status de tarefas.' },
      { nome: 'Gestor · Feedback de Qualidade', desc: 'Feedback específico, com exemplo e comportamento observável, equilibrando reconhecimento e desenvolvimento — nada de "tá indo bem".' },
      { nome: 'Gestor · Desenvolvimento e Carreira', desc: 'Fala de crescimento, PDI e próximos desafios do liderado — o que diferencia uma 1:1 de uma reunião de tarefas.' },
      { nome: 'Gestor · Remoção de Bloqueios', desc: 'Investiga o que está travando o liderado e assume ações concretas para destravar — a parte que só o gestor pode fazer.' },
      { nome: 'Gestor · Segurança Psicológica', desc: 'Quando o liderado expõe problema ou vulnerabilidade, acolhe e investiga — em vez de minimizar ou transformar em cobrança.' },
      { nome: 'Gestor · Reconhecimento', desc: 'Valoriza entregas e comportamentos específicos do liderado — não só aponta o que falta.' },
      { nome: 'Gestor · Follow-through', desc: 'Retoma os combinados da 1:1 anterior e fecha esta com ações claras: o quê, quem e quando.' },
      { nome: 'Liderado · Participação Ativa', desc: 'Traz pauta própria, responde com profundidade e contribui — em vez de ser monossilábico e reativo.' },
      { nome: 'Liderado · Transparência', desc: 'Expõe dificuldades, riscos e sentimentos reais — em vez de responder "tá tudo bem" e evitar os assuntos difíceis.' },
      { nome: 'Liderado · Protagonismo', desc: 'Propõe soluções, assume responsabilidade e pede o que precisa — em vez de apenas relatar ou reclamar.' },
      { nome: 'Liderado · Receptividade a Feedback', desc: 'Acolhe e explora o feedback recebido — em vez de se defender, justificar ou desconversar.' },
      { nome: 'Liderado · Clareza de Prioridades', desc: 'Sabe quais são suas metas e prioridades — e cobra alinhamento quando algo não está claro.' },
    ],
    boa: [
      'Liderado fala mais que o gestor, com espaço real para se expor',
      'Feedback com exemplo concreto e reconhecimento de entregas específicas',
      'Conversa vai além do operacional: carreira, motivação e desenvolvimento na pauta',
      'Bloqueios investigados e assumidos pelo gestor como ação dele',
      'Sinais de desmotivação ou sobrecarga percebidos e tratados na hora',
      'Combinados claros dos dois lados, retomando os da 1:1 anterior',
    ],
    ma: [
      '1:1 vira status report: só tarefas, prazos e cobrança',
      'Gestor fala o tempo todo e o liderado só concorda',
      'Liderado desabafa ou sinaliza desconforto e o gestor muda de assunto',
      'Nenhuma menção a carreira, crescimento ou reconhecimento',
      'Feedback genérico, sem exemplo ("continua assim")',
      'Reunião termina sem combinados — e os da anterior nem foram retomados',
    ],
    ancoras: [
      { faixa: '0–3', cor: '#dc3545', texto: 'Monólogo do gestor ou status report puro: liderado sem espaço, sinais de risco ignorados, sem combinados' },
      { faixa: '4–6', cor: '#fd7e14', texto: 'Acompanhamento correto mas transacional: tarefas em dia, porém pouca escuta e nenhum desenvolvimento' },
      { faixa: '7–8', cor: '#0d6efd', texto: '1:1 de verdade: escuta ativa, feedback com exemplos, bloqueios tratados e combinados claros' },
      { faixa: '9–10', cor: '#198754', texto: 'Gestão exemplar: carreira na pauta, segurança psicológica visível, liderado participativo e reconhecido' },
    ],
    beneficios: [
      { icon: 'fa-user-check',      titulo: 'Participação visível',       texto: 'Você enxerga quem participa ativamente das 1:1s e quem só assiste — engajamento deixa de ser impressão.' },
      { icon: 'fa-user-tie',        titulo: 'Qualidade de gestão medida', texto: 'Escuta, feedback e desenvolvimento viram indicadores — você sabe se cada gestor está gerindo de verdade.' },
      { icon: 'fa-fire-extinguisher', titulo: 'Risco de perda de talento', texto: 'Desmotivação, sobrecarga e sinais de burnout geram alerta antes de virarem pedido de demissão.' },
      { icon: 'fa-star',            titulo: 'Talentos revelados',         texto: 'Liderados prontos para mais responsabilidade e ideias de melhoria ficam registrados como oportunidade.' },
      { icon: 'fa-list-check',      titulo: 'Combinados acompanhados',    texto: 'O que foi combinado na 1:1 fica registrado e é retomado na seguinte — a conversa não evapora.' },
      { icon: 'fa-chart-line',      titulo: 'Evolução dos dois lados',    texto: 'Radar do gestor mede liderança; radar do liderado mede participação — os dois evoluem com dados.' },
    ],
  },
};

// ── Aba "O que a Comece Ganha" ─────────────────────────────────────────────
const EMPRESA_INFO = {
  label: 'O que a Comece Ganha',
  cor: '#00205b',
  icone: 'fa-rocket',
};

const EMPRESA_BENEFICIOS = [
  { icon: 'fa-database',       titulo: 'Memória institucional',            texto: 'Nada do que é combinado em reunião se perde. Cada conversa vira registro estruturado, pesquisável e comparável — mesmo com troca de operador ou gestor, o histórico do cliente permanece.' },
  { icon: 'fa-scale-balanced', titulo: 'Gestão baseada em evidência',      texto: 'O gestor deixa de depender de percepção: enxerga quem performa, em qual competência cada um precisa de apoio e quais reuniões merecem atenção — sem assistir a nenhuma delas.' },
  { icon: 'fa-graduation-cap', titulo: 'Desenvolvimento contínuo do time', texto: 'Cada operador recebe feedback objetivo após cada reunião e treinamento personalizado sobre os próprios pontos de melhoria. O coaching escala sem consumir agenda de gestor.' },
  { icon: 'fa-handshake',      titulo: 'Atendimento consultivo de verdade', texto: 'As análises mostram se estamos apenas "entregando o serviço" ou diagnosticando dores, propondo soluções e conduzindo o cliente — o que sustenta o posicionamento consultivo da Comece Hub.' },
  { icon: 'fa-file-signature', titulo: 'Mais contratos fechados',          texto: 'O funil comercial se alimenta sozinho a partir das conversas: objeções mapeadas, interesse medido, follow-ups cobrados e estratégia de fechamento sugerida pela Memor.IA para cada negociação.' },
  { icon: 'fa-shield-heart',   titulo: 'Retenção proativa de clientes',    texto: 'Os sinais que antecedem um cancelamento (silêncio prolongado, insatisfação na fala, objeções repetidas) são detectados e priorizados antes de virarem pedido de rescisão.' },
  { icon: 'fa-list-check',     titulo: 'Execução garantida',               texto: 'Os próximos passos combinados em reunião viram tarefas no Google Tasks dos responsáveis automaticamente — o ciclo fecha: conversa → compromisso → cobrança.' },
  { icon: 'fa-bolt',           titulo: 'Decisão em minutos',               texto: 'Diagnóstico completo da operação de um time sob demanda e chat de Intelligence em linguagem natural — perguntas que levavam dias de apuração são respondidas na hora.' },
];

const EMPRESA_SETORES = [
  { setor: 'Contábil · BPO · Fiscal · DP', cor: '#d610b5', icone: 'fa-handshake',
    ganhos: ['Qualidade consultiva medida em cada atendimento', 'Dores dos clientes rastreadas entre reuniões', 'Insatisfação detectada antes do cancelamento', 'Oportunidades de expansão viram leads no funil'] },
  { setor: 'Comercial / Vendas', cor: '#0d6efd', icone: 'fa-funnel-dollar',
    ganhos: ['Funil automático com chance de fechamento por cliente', 'Objeções categorizadas: o que mais trava as vendas', 'Estratégia de fechamento por negociação', 'Follow-up cobrado — nenhum lead esfria em silêncio'] },
  { setor: 'Reunião Vision', cor: '#0a8fa8', icone: 'fa-chart-pie',
    ganhos: ['Cenário de cada cliente etiquetado (Baixo Risco a Crítico)', 'Assessoria de performance medida, não só leitura de relatório', 'Adoção do Vision acompanhada', 'Demandas para a Inteligência de Dados capturadas'] },
  { setor: 'Reforma Tributária', cor: '#fd7e14', icone: 'fa-landmark',
    ganhos: ['Prontidão de cada cliente para a reforma', 'Informação técnica errada vira alerta', 'Projetos de adequação viram oportunidade', 'Planos de ação por fase do cronograma'] },
  { setor: 'Gestão de Pessoas', cor: '#6610f2', icone: 'fa-people-arrows',
    ganhos: ['Qualidade das 1:1s medida: escuta, feedback, desenvolvimento', 'Engajamento de cada liderado visível', 'Risco de perda de talento antecipado', 'Talentos prontos para crescer identificados'] },
  { setor: 'Diretoria / Controladoria', cor: '#00205b', icone: 'fa-chess-king',
    ganhos: ['Tempo de gestão devolvido e desenvolvimento de time acelerado', 'Crescimento da empresa (tanto em vendas quanto em pessoas)', 'Visão executiva de todos os times em um painel', 'Diagnóstico da operação gerado sob demanda', 'Decisões de treinamento e alocação com dados', 'Padrão único de qualidade em toda a empresa'] },
];

const EMPRESA_IMPLANTACAO = [
  { passo: 1, titulo: 'Apresentação aos gestores',   texto: 'Reunião de apresentação do produto e do plano de implantação.', status: 'feito' },
  { passo: 2, titulo: 'Homologação e acessos',       texto: 'Definição dos usuários do piloto e liberação do menu Memor.IA.', status: 'andamento' },
  { passo: 3, titulo: 'Treinamento dos participantes', texto: 'Como analisar reuniões, navegar nos painéis e usar as ferramentas de IA.', status: 'andamento' },
  { passo: 4, titulo: 'Fase 1 — Observabilidade',    texto: 'Piloto nos setores de Inteligência de Dados e Comercial: análises acompanhadas para validar qualidade e utilidade.', status: 'andamento' },
  { passo: 5, titulo: 'Leitura dos resultados',      texto: 'Adoção, precisão das análises e valor percebido — o que gerou ação de verdade.', status: 'pendente' },
  { passo: 6, titulo: 'Expansão para a empresa',     texto: 'Com os aprendizados da Fase 1, liberação gradual para os demais setores.', status: 'pendente' },
];

// ── Aba "Custo" ────────────────────────────────────────────────────────────
const CUSTO_INFO = { label: 'Custo', cor: '#198754', icone: 'fa-coins' };

// Câmbio aproximado para exibição (o cálculo real é feito em USD)
const USD_BRL = 5.5;

const FUNC_LABELS = {
  analise_reuniao:      { label: 'Análise de reunião',                icon: 'fa-video' },
  preparacao_reuniao:   { label: 'Preparação de reunião',             icon: 'fa-door-open' },
  treinamento:          { label: 'Treinamento do operador',           icon: 'fa-graduation-cap' },
  estrategia_fechamento:{ label: 'Estratégia de fechamento/expansão', icon: 'fa-chess' },
  diagnostico_time:     { label: 'Diagnóstico do time (gestor)',      icon: 'fa-users-gear' },
  audio_tts:            { label: 'Narração em áudio',                 icon: 'fa-headphones' },
  validacao_interna:    { label: 'Validação de reunião interna',      icon: 'fa-user-check' },
  copiloto_realtime:    { label: 'Copiloto — sugestões ao vivo',      icon: 'fa-headset' },
  copiloto_stt:         { label: 'Copiloto — transcrição ao vivo',    icon: 'fa-microphone-lines' },
  pergunta_cliente:     { label: 'Perguntas sobre cliente',           icon: 'fa-comments' },
};

const brl = (usd) =>
  'R$ ' + ((usd || 0) * USD_BRL).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Comparativo de LLMs: preços oficiais por 1M de tokens (US$)
const LLMS_COMPARACAO = [
  { nome: 'Gemini 2.5 Flash', entrada: 0.30, saida: 2.50, atual: true },
  { nome: 'Claude Haiku 4.5', entrada: 1.00, saida: 5.00 },
  { nome: 'Claude Sonnet 5',  entrada: 3.00, saida: 15.00, obs: 'promocional US$ 2/10 por 1M até 31/08/2026' },
  { nome: 'Claude Opus 4.8',  entrada: 5.00, saida: 25.00 },
];

const REGRAS_GERAIS = [
  { icon: 'fa-quote-left',   titulo: 'Tudo com evidência',       texto: 'Cada avaliação de competência vem com uma frase real da transcrição que a justifica — nada de opinião solta.' },
  { icon: 'fa-balance-scale', titulo: 'Realista, não otimista',  texto: 'A Memor.IA é instruída a ser crítica. Nota alta precisa ser merecida; performance neutra vem como "Sem destaques".' },
  { icon: 'fa-minus-circle', titulo: '"Não se aplica" existe',    texto: 'Se a reunião não deu oportunidade de exercer uma competência, ela não conta contra o operador.' },
  { icon: 'fa-gem',          titulo: 'Concorrente ≠ objeção',     texto: 'Cliente reclamando do fornecedor antigo não penaliza ninguém — vira oportunidade de expansão registrada.' },
  { icon: 'fa-random',       titulo: 'Reuniões mistas',           texto: 'Se um operador exercer competências de outro papel (ex.: contador vendendo), elas são avaliadas também e alimentam o radar certo.' },
  { icon: 'fa-star',         titulo: 'Talento além do setor',     texto: 'Competências de outra área com média ≥ 7 em 2+ avaliações aparecem como talento no painel do operador.' },
];

export default function Memora_documentacao() {
  const navigate = useNavigate();
  const [tipoAtivo, setTipoAtivo] = useState('servico');
  const [custoDados, setCustoDados] = useState(null);
  const [carregandoCusto, setCarregandoCusto] = useState(false);
  const [infraDesc, setInfraDesc] = useState('');
  const [infraValor, setInfraValor] = useState('');
  const [salvandoInfra, setSalvandoInfra] = useState(false);
  const [ehAdm, setEhAdm] = useState(false);
  const tipo = TIPOS[tipoAtivo];

  // A aba "Custo" expõe gasto por chamada, tokens e operador+horário — dado
  // operacional interno restrito ao grupo 'adm' (mesmo padrão de Historico_auditoria.js).
  useEffect(() => {
    const token = localStorage.getItem('access');
    axios.get(`${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (r.data.success) setEhAdm((r.data.grupo || '').toLowerCase().trim() === 'adm'); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!ehAdm && tipoAtivo === 'custo') setTipoAtivo('servico');
  }, [ehAdm, tipoAtivo]);

  // Lançamentos de custo de infraestrutura (adm)
  const lancarInfra = () => {
    if (!infraDesc.trim() || !infraValor) return;
    setSalvandoInfra(true);
    const token = localStorage.getItem('access');
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/reunioes/custo-infra/`,
        { descricao: infraDesc.trim(), valor_brl: infraValor },
        { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { setInfraDesc(''); setInfraValor(''); setCustoDados(null); })
      .catch(() => {})
      .finally(() => setSalvandoInfra(false));
  };

  const excluirInfra = (id) => {
    const token = localStorage.getItem('access');
    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/reunioes/custo-infra/`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { id },
      })
      .then(() => setCustoDados(null))
      .catch(() => {});
  };

  // Carrega o custo em tempo real ao abrir a aba Custo
  useEffect(() => {
    if (tipoAtivo !== 'custo' || custoDados) return;
    setCarregandoCusto(true);
    const token = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/reunioes/custo-ia/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (res.data.success) setCustoDados(res.data); })
      .catch(() => {})
      .finally(() => setCarregandoCusto(false));
  }, [tipoAtivo, custoDados]);

  return (
    <div className="das-page">
      <div className="container-fluid mt-4">

        {/* ── Header ── */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Documentação — Memor.IA
                      </span>
                      <span className="badge" style={{ fontSize: '15px', fontWeight: '500', backgroundColor: '#00205b', color: '#fff' }}>
                        <i className="fas fa-book me-1"></i>Pilares das Análises
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-book-open header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Como a Memor.IA Avalia Cada Setor</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-robot me-2"></i>
                      As competências, os critérios de nota e o que define uma boa (ou má) entrega em Serviço, Vendas, Gestão de Pessoas, Vision e Reforma Tributária
                    </p>
                  </div>
                  <div className="d-none d-md-flex align-items-center justify-content-center" style={{ width: 280, flexShrink: 0 }}>
                    <MemoraAnimacao tamanho={280} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Seletor de tipo ── */}
        <div className="d-flex gap-2 flex-wrap mb-4">
          {Object.entries(TIPOS).map(([key, t]) => (
            <button
              key={key}
              className="btn"
              onClick={() => setTipoAtivo(key)}
              style={{
                background: tipoAtivo === key ? t.cor : '#fff',
                color: tipoAtivo === key ? '#fff' : t.cor,
                border: `2px solid ${t.cor}`,
                fontWeight: 600,
              }}
            >
              <i className={`fas ${t.icone} me-2`}></i>{t.label}
            </button>
          ))}
          <button
            className="btn"
            onClick={() => setTipoAtivo('empresa')}
            style={{
              background: tipoAtivo === 'empresa' ? EMPRESA_INFO.cor : '#fff',
              color: tipoAtivo === 'empresa' ? '#fff' : EMPRESA_INFO.cor,
              border: `2px solid ${EMPRESA_INFO.cor}`,
              fontWeight: 600,
            }}
          >
            <i className={`fas ${EMPRESA_INFO.icone} me-2`}></i>{EMPRESA_INFO.label}
          </button>
          {ehAdm && (
            <button
              className="btn"
              onClick={() => setTipoAtivo('custo')}
              style={{
                background: tipoAtivo === 'custo' ? CUSTO_INFO.cor : '#fff',
                color: tipoAtivo === 'custo' ? '#fff' : CUSTO_INFO.cor,
                border: `2px solid ${CUSTO_INFO.cor}`,
                fontWeight: 600,
              }}
            >
              <i className={`fas ${CUSTO_INFO.icone} me-2`}></i>{CUSTO_INFO.label}
            </button>
          )}
        </div>

        {tipoAtivo === 'custo' && ehAdm ? (
          <>
            {/* ── Como o custo é medido ── */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: `4px solid ${CUSTO_INFO.cor}` }}>
              <div className="card-body py-4">
                <p className="mb-0" style={{ fontSize: '1.02rem', lineHeight: '1.75' }}>
                  O custo abaixo é <strong>medido em tempo real</strong>: a cada chamada de IA (análise de reunião,
                  preparação, treinamento, diagnóstico, áudio...), o Gemini reporta exatamente quantos tokens foram
                  processados, e o sistema grava a quantidade multiplicada pela tabela oficial de preços. Os valores
                  em reais usam câmbio aproximado de <strong>R$ {USD_BRL.toFixed(2).replace('.', ',')}</strong> por dólar;
                  a conferência oficial é feita mensalmente no faturamento do Google Cloud.
                </p>
              </div>
            </div>

            {carregandoCusto ? (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body text-center py-5">
                  <div className="spinner-border mb-3" style={{ color: CUSTO_INFO.cor, width: '2.5rem', height: '2.5rem' }}></div>
                  <p className="text-muted mb-0">Calculando o custo em tempo real...</p>
                </div>
              </div>
            ) : !custoDados ? (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body text-center py-5">
                  <i className="fas fa-plug-circle-xmark fa-2x text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">
                    Ainda não há dados de uso registrados — o custo passa a ser medido a partir das próximas chamadas de IA.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* ── KPIs do mês ── */}
                <div className="row g-3 mb-4">
                  {[
                    { label: 'Custo do mês', valor: brl(custoDados.mes_atual.custo_usd), sub: `US$ ${custoDados.mes_atual.custo_usd.toFixed(2)}`, icon: 'fa-coins' },
                    { label: 'Chamadas de IA no mês', valor: custoDados.mes_atual.chamadas.toLocaleString('pt-BR'), sub: 'análises, áudios, estratégias...', icon: 'fa-bolt' },
                    { label: 'Tokens processados', valor: (custoDados.mes_atual.tokens / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + 'k', sub: 'entrada + saída + raciocínio', icon: 'fa-microchip' },
                    { label: 'Custo médio por análise', valor: brl(custoDados.custo_medio_analise_usd), sub: 'por reunião analisada', icon: 'fa-video' },
                  ].map((k, i) => (
                    <div key={i} className="col-6 col-lg-3">
                      <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${CUSTO_INFO.cor}` }}>
                        <div className="card-body py-3">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <i className={`fas ${k.icon}`} style={{ color: CUSTO_INFO.cor }}></i>
                            <span className="fw-semibold text-dark" style={{ fontSize: '0.8rem' }}>{k.label}</span>
                          </div>
                          <div className="fw-bold lh-1 mb-1" style={{ fontSize: '1.5rem', color: CUSTO_INFO.cor }}>{k.valor}</div>
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>{k.sub}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Custo por funcionalidade ── */}
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white" style={{ borderBottom: `2px solid ${CUSTO_INFO.cor}` }}>
                    <h6 className="mb-0">
                      <i className="fas fa-chart-pie me-2" style={{ color: CUSTO_INFO.cor }}></i>
                      Custo por Funcionalidade — Mês Atual
                    </h6>
                    <small className="text-muted">Onde os créditos de IA estão sendo usados</small>
                  </div>
                  <div className="card-body">
                    {custoDados.por_funcionalidade.length === 0 ? (
                      <p className="text-muted small mb-0">Nenhum uso registrado neste mês ainda.</p>
                    ) : (
                      custoDados.por_funcionalidade.map((f, i) => {
                        const info = FUNC_LABELS[f.funcionalidade] || { label: f.funcionalidade, icon: 'fa-robot' };
                        const total = custoDados.mes_atual.custo_usd || 1;
                        const pct = Math.round((f.custo_usd / total) * 100);
                        return (
                          <div key={i} className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span style={{ fontSize: '0.9rem' }}>
                                <i className={`fas ${info.icon} me-2`} style={{ color: CUSTO_INFO.cor, width: 18 }}></i>
                                <strong>{info.label}</strong>
                                <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>{f.chamadas} chamada{f.chamadas !== 1 ? 's' : ''}</span>
                              </span>
                              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{brl(f.custo_usd)}</span>
                            </div>
                            <div className="progress" style={{ height: 6 }}>
                              <div className="progress-bar" style={{ width: `${Math.max(pct, 2)}%`, background: CUSTO_INFO.cor }}></div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* ── Comparativo com outros LLMs ── */}
                {custoDados.tokens_texto_mes && (
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-white" style={{ borderBottom: `2px solid ${CUSTO_INFO.cor}` }}>
                      <h6 className="mb-0">
                        <i className="fas fa-code-compare me-2" style={{ color: CUSTO_INFO.cor }}></i>
                        E se Fosse Outro LLM? — Comparativo do Mês
                      </h6>
                      <small className="text-muted">
                        O mesmo uso de texto deste mês ({(custoDados.tokens_texto_mes.entrada / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}k tokens de entrada
                        + {(custoDados.tokens_texto_mes.saida / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}k de saída),
                        calculado na tabela de preços de cada modelo — narração em áudio fora da conta (os modelos Claude não geram áudio)
                      </small>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th className="ps-3">Modelo</th>
                              <th className="text-end">Entrada (US$/1M)</th>
                              <th className="text-end">Saída (US$/1M)</th>
                              <th className="text-end">Custo do mês (só texto)</th>
                              <th className="text-end pe-3">vs. atual</th>
                            </tr>
                          </thead>
                          <tbody>
                            {LLMS_COMPARACAO.map((m, i) => {
                              const t = custoDados.tokens_texto_mes;
                              const custo = m.atual
                                ? t.custo_usd
                                : (t.entrada * m.entrada + t.saida * m.saida) / 1000000;
                              const base = t.custo_usd || 0;
                              const razao = base > 0 ? custo / base : null;
                              return (
                                <tr key={i} style={m.atual ? { background: '#f0faf5' } : undefined}>
                                  <td className="ps-3 fw-semibold">
                                    {m.nome}
                                    {m.atual && (
                                      <span className="badge ms-2" style={{ background: CUSTO_INFO.cor, fontSize: '0.68rem' }}>ATUAL</span>
                                    )}
                                    {m.obs && (
                                      <i className="fas fa-info-circle ms-2 text-muted" style={{ fontSize: '0.75rem' }} title={m.obs}></i>
                                    )}
                                  </td>
                                  <td className="text-end">US$ {m.entrada.toFixed(2)}</td>
                                  <td className="text-end">US$ {m.saida.toFixed(2)}</td>
                                  <td className="text-end fw-bold">{brl(custo)}</td>
                                  <td className="text-end pe-3">
                                    {m.atual ? (
                                      <span className="text-muted">—</span>
                                    ) : razao === null ? (
                                      <span className="text-muted">—</span>
                                    ) : (
                                      <span className={`badge ${razao > 1 ? 'bg-danger' : 'bg-success'}`} style={{ fontSize: '0.78rem' }}>
                                        {razao > 1 ? `${razao.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}× mais caro` : `${(1 / razao).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}× mais barato`}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-3 py-2 text-muted" style={{ fontSize: '0.78rem' }}>
                        <i className="fas fa-lightbulb me-1"></i>
                        Comparação de preço por token — considera <strong>apenas as chamadas de texto</strong> (a narração em
                        áudio fica de fora, pois os modelos Claude não geram áudio). Por isso o valor aqui é menor que o
                        total do Histórico Mensal abaixo, que inclui o áudio. Também não considera diferenças de qualidade
                        entre os modelos nem o preço promocional do Sonnet 5 (US$ 2/10 por 1M até 31/08/2026, ~33% menor).
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Histórico mensal ── */}
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white" style={{ borderBottom: `2px solid ${CUSTO_INFO.cor}` }}>
                    <h6 className="mb-0">
                      <i className="fas fa-calendar me-2" style={{ color: CUSTO_INFO.cor }}></i>
                      Histórico Mensal
                    </h6>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">Mês</th>
                          <th className="text-center">Chamadas de IA</th>
                          <th className="text-end pe-3">Custo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {custoDados.historico.map((h, i) => (
                          <tr key={i}>
                            <td className="ps-3 fw-semibold">{h.mes.split('-').reverse().join('/')}</td>
                            <td className="text-center">{h.chamadas.toLocaleString('pt-BR')}</td>
                            <td className="text-end pe-3 fw-semibold">{brl(h.custo_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ── Últimas chamadas (detalhe) ── */}
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white" style={{ borderBottom: `2px solid ${CUSTO_INFO.cor}` }}>
                    <h6 className="mb-0">
                      <i className="fas fa-list me-2" style={{ color: CUSTO_INFO.cor }}></i>
                      Últimas Chamadas de IA ({(custoDados.chamadas_recentes || []).length})
                    </h6>
                    <small className="text-muted">Tokens usados por chamada, com operador e horário de Brasília</small>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th className="ps-3">Data / Hora</th>
                            <th>Operador</th>
                            <th>Funcionalidade</th>
                            <th>Referência</th>
                            <th className="text-end">Tokens entrada</th>
                            <th className="text-end">Tokens saída</th>
                            <th className="text-end">Custo</th>
                            <th className="text-end">Haiku 4.5</th>
                            <th className="text-end">Sonnet 5</th>
                            <th className="text-end pe-3">Opus 4.8</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(custoDados.chamadas_recentes || []).map((c, i) => {
                            const info = FUNC_LABELS[c.funcionalidade] || { label: c.funcionalidade, icon: 'fa-robot' };
                            const ehAudio = c.funcionalidade === 'audio_tts';
                            const custoClaude = (m) => ehAudio
                              ? null
                              : (c.tokens_entrada * m.entrada + (c.tokens_saida + c.tokens_pensamento) * m.saida) / 1000000;
                            const claudeCols = [LLMS_COMPARACAO[1], LLMS_COMPARACAO[2], LLMS_COMPARACAO[3]].map(custoClaude);
                            return (
                              <tr key={i}>
                                <td className="ps-3" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{c.data_hora}</td>
                                <td className="fw-semibold" style={{ fontSize: '0.85rem' }}>{c.operador}</td>
                                <td style={{ fontSize: '0.85rem' }}>
                                  <i className={`fas ${info.icon} me-1`} style={{ color: CUSTO_INFO.cor }}></i>
                                  {info.label}
                                </td>
                                <td className="text-muted" style={{ fontSize: '0.82rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.referencia}>
                                  {c.referencia || '—'}
                                </td>
                                <td className="text-end" style={{ fontSize: '0.85rem' }}>
                                  {c.tokens_entrada.toLocaleString('pt-BR')}
                                </td>
                                <td className="text-end" style={{ fontSize: '0.85rem' }}
                                  title={`Resposta: ${c.tokens_saida.toLocaleString('pt-BR')} · Raciocínio: ${c.tokens_pensamento.toLocaleString('pt-BR')}`}>
                                  {(c.tokens_saida + c.tokens_pensamento).toLocaleString('pt-BR')}
                                </td>
                                <td className="text-end fw-semibold" style={{ fontSize: '0.85rem' }}>{brl(c.custo_usd)}</td>
                                {claudeCols.map((v, ci) => (
                                  <td key={ci} className={`text-end text-muted ${ci === 2 ? 'pe-3' : ''}`} style={{ fontSize: '0.82rem' }}>
                                    {v === null ? '—' : brl(v)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                {/* ── Custo de servidores (rateio automático) ── */}
                {custoDados.servidor && (() => {
                  const srv = custoDados.servidor;
                  const custoIaBrl = custoDados.mes_atual.custo_usd * USD_BRL;
                  const totalGeral = custoIaBrl + (srv.custo_memora_brl || 0);
                  return (
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-header bg-white" style={{ borderBottom: `2px solid ${CUSTO_INFO.cor}` }}>
                        <h6 className="mb-0">
                          <i className="fas fa-server me-2" style={{ color: CUSTO_INFO.cor }}></i>
                          Custo de Servidores — Rateio Automático
                        </h6>
                        <small className="text-muted">
                          O sistema conta as requisições de cada área: a fração feita pelas rotas da
                          Memor.IA define quanto do custo dos servidores é atribuído a ela
                        </small>
                      </div>
                      <div className="card-body">
                        <div className="row g-3 mb-3">
                          {[
                            {
                              label: 'Uso do servidor pela Memor.IA',
                              valor: srv.requisicoes_total > 0 ? `${srv.pct_memora.toLocaleString('pt-BR')}%` : '—',
                              sub: srv.requisicoes_total > 0
                                ? `das requisições do servidor no mês (${srv.requisicoes_memora.toLocaleString('pt-BR')} de ${srv.requisicoes_total.toLocaleString('pt-BR')})`
                                : 'medição inicia com as próximas requisições',
                              icon: 'fa-percent',
                            },
                            {
                              label: 'Servidores no mês (lançado)',
                              valor: 'R$ ' + srv.custo_infra_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
                              sub: `${srv.lancamentos.length} lançamento${srv.lancamentos.length !== 1 ? 's' : ''}`,
                              icon: 'fa-server',
                            },
                            {
                              label: 'Parte da Memor.IA (rateada)',
                              valor: 'R$ ' + srv.custo_memora_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
                              sub: 'custo dos servidores × % de uso',
                              icon: 'fa-brain',
                            },
                            {
                              label: 'Custo total da Memor.IA no mês',
                              valor: 'R$ ' + totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
                              sub: 'IA + servidores rateados',
                              icon: 'fa-sack-dollar',
                              destaque: true,
                            },
                          ].map((k, i) => (
                            <div key={i} className="col-6 col-lg-3">
                              <div className="p-3 rounded-3 h-100" style={{
                                background: k.destaque ? '#f0faf5' : '#f8f9fa',
                                border: k.destaque ? `1px solid ${CUSTO_INFO.cor}40` : '1px solid #eef0f2',
                              }}>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <i className={`fas ${k.icon}`} style={{ color: CUSTO_INFO.cor, fontSize: '0.8rem' }}></i>
                                  <span className="fw-semibold text-dark" style={{ fontSize: '0.76rem' }}>{k.label}</span>
                                </div>
                                <div className="fw-bold lh-1 mb-1" style={{ fontSize: '1.3rem', color: CUSTO_INFO.cor }}>{k.valor}</div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>{k.sub}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Lançamentos do mês */}
                        {srv.lancamentos.length > 0 && (
                          <div className="mb-3">
                            {srv.lancamentos.map((l) => (
                              <div key={l.id} className="d-flex justify-content-between align-items-center py-1 px-2 rounded mb-1" style={{ background: '#f8f9fa', fontSize: '0.87rem' }}>
                                <span><i className="fas fa-receipt me-2 text-muted"></i>{l.descricao}</span>
                                <span className="d-flex align-items-center gap-2">
                                  <strong>R$ {l.valor_brl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                                  {custoDados.pode_editar && (
                                    <button className="btn btn-link btn-sm p-0 text-danger" title="Excluir lançamento"
                                      onClick={() => excluirInfra(l.id)}>
                                      <i className="fas fa-times"></i>
                                    </button>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Form de lançamento (adm) */}
                        {custoDados.pode_editar ? (
                          <div className="d-flex gap-2 flex-wrap align-items-center">
                            <input
                              className="form-control form-control-sm"
                              style={{ maxWidth: 320 }}
                              placeholder="Descrição (ex.: Cloud Run — backend)"
                              value={infraDesc}
                              onChange={(e) => setInfraDesc(e.target.value)}
                            />
                            <input
                              className="form-control form-control-sm"
                              style={{ maxWidth: 140 }}
                              placeholder="Valor (R$)"
                              value={infraValor}
                              onChange={(e) => setInfraValor(e.target.value)}
                            />
                            <button
                              className="btn btn-sm fw-semibold"
                              onClick={lancarInfra}
                              disabled={salvandoInfra || !infraDesc.trim() || !infraValor}
                              style={{ background: CUSTO_INFO.cor, border: 'none', color: '#fff' }}
                            >
                              {salvandoInfra
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <><i className="fas fa-plus me-1"></i>Lançar custo do mês</>}
                            </button>
                            <small className="text-muted">
                              Pegue os valores no faturamento do Google Cloud (Cloud Run backend, frontend, banco...)
                            </small>
                          </div>
                        ) : srv.lancamentos.length === 0 && (
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            O custo dos servidores deste mês ainda não foi lançado pelo administrador.
                          </small>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* ── Copiloto em tempo real ── */}
            <CustoCopiloto brl={brl} cor={CUSTO_INFO.cor} usdBrl={USD_BRL} />

            {/* ── Custo × valor ── */}
            <div className="card border-0 shadow-sm mb-4" style={{ background: '#f0faf5', borderLeft: `4px solid ${CUSTO_INFO.cor}` }}>
              <div className="card-body py-4">
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-scale-unbalanced-flip me-2" style={{ color: CUSTO_INFO.cor }}></i>
                  Custo × Valor
                </h6>
                <p className="mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.7' }}>
                  Analisar uma reunião custa <strong>centavos</strong> — enquanto um gestor assistindo a mesma gravação
                  custaria uma hora de agenda. Uma única insatisfação detectada antes do cancelamento, uma venda fechada
                  com a estratégia da Memor.IA ou um talento retido paga <strong>meses</strong> de uso da plataforma inteira.
                </p>
              </div>
            </div>
          </>
        ) : tipoAtivo === 'empresa' ? (
          <>
            {/* ── O que é / por quê ── */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: `4px solid ${EMPRESA_INFO.cor}` }}>
              <div className="card-body py-4">
                <p className="mb-0" style={{ fontSize: '1.02rem', lineHeight: '1.75' }}>
                  A <strong>Memor.IA</strong> transforma as reuniões gravadas da Comece Hub em informação de gestão:
                  qualidade do atendimento, desempenho de cada operador, oportunidades comerciais em aberto e sinais
                  de risco de perda de clientes. Ela responde a perguntas que hoje dependem de memória e percepção
                  individual — <strong>como foi a reunião? como o time está atendendo? onde estão as oportunidades?
                  quem está em risco?</strong> — de forma automática, consistente e disponível na hora.
                </p>
              </div>
            </div>

            {/* ── Benefícios macro ── */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white" style={{ borderBottom: `2px solid ${EMPRESA_INFO.cor}` }}>
                <h6 className="mb-0">
                  <i className="fas fa-trophy me-2" style={{ color: EMPRESA_INFO.cor }}></i>
                  O que a Empresa Ganha
                </h6>
                <small className="text-muted">Os ganhos estruturais de colocar a Memor.IA em todas as reuniões</small>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {EMPRESA_BENEFICIOS.map((b, i) => (
                    <div key={i} className="col-md-6 col-lg-4">
                      <div className="d-flex gap-3 p-3 rounded-3 h-100" style={{ background: '#f8f9fa' }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                          background: `${EMPRESA_INFO.cor}14`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <i className={`fas ${b.icon}`} style={{ color: EMPRESA_INFO.cor }}></i>
                        </div>
                        <div>
                          <strong style={{ fontSize: '0.92rem' }}>{b.titulo}</strong>
                          <p className="mb-0 text-muted" style={{ fontSize: '0.84rem' }}>{b.texto}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Ganhos por setor ── */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white" style={{ borderBottom: `2px solid ${EMPRESA_INFO.cor}` }}>
                <h6 className="mb-0">
                  <i className="fas fa-sitemap me-2" style={{ color: EMPRESA_INFO.cor }}></i>
                  Ganhos por Setor
                </h6>
                <small className="text-muted">O que muda na rotina de cada área da Comece Hub</small>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {EMPRESA_SETORES.map((s, i) => (
                    <div key={i} className="col-md-6 col-xl-4">
                      <div className="p-3 rounded-3 h-100" style={{ background: '#f8f9fa', borderTop: `3px solid ${s.cor}` }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <i className={`fas ${s.icone}`} style={{ color: s.cor }}></i>
                          <strong style={{ fontSize: '0.95rem' }}>{s.setor}</strong>
                        </div>
                        {s.ganhos.map((g, gi) => (
                          <div key={gi} className="d-flex gap-2 mb-1">
                            <i className="fas fa-check text-success flex-shrink-0" style={{ marginTop: 4, fontSize: '0.75rem' }}></i>
                            <span style={{ fontSize: '0.85rem' }}>{g}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Planejamento de implantação ── */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white" style={{ borderBottom: `2px solid ${EMPRESA_INFO.cor}` }}>
                <h6 className="mb-0">
                  <i className="fas fa-route me-2" style={{ color: EMPRESA_INFO.cor }}></i>
                  Planejamento de Implantação
                </h6>
                <small className="text-muted">Implantação gradual e assistida — cada etapa passa pelo crivo da controladoria</small>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {EMPRESA_IMPLANTACAO.map((e, i) => {
                    const st = e.status === 'feito'
                      ? { cor: '#198754', label: 'Concluído', icon: 'fa-check' }
                      : e.status === 'andamento'
                        ? { cor: '#0d6efd', label: 'Em andamento', icon: 'fa-rotate' }
                        : { cor: '#6c757d', label: 'Próximo', icon: 'fa-hourglass-half' };
                    return (
                      <div key={i} className="col-md-6 col-lg-4">
                        <div className="p-3 rounded-3 h-100" style={{ background: '#f8f9fa', borderLeft: `3px solid ${st.cor}` }}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="badge rounded-pill" style={{
                              background: EMPRESA_INFO.cor, width: 26, height: 26,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>{e.passo}</span>
                            <span className="badge" style={{ background: `${st.cor}18`, color: st.cor }}>
                              <i className={`fas ${st.icon} me-1`}></i>{st.label}
                            </span>
                          </div>
                          <strong style={{ fontSize: '0.92rem' }}>{e.titulo}</strong>
                          <p className="mb-0 text-muted" style={{ fontSize: '0.84rem' }}>{e.texto}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="alert alert-light border mt-3 mb-0" style={{ fontSize: '0.87rem' }}>
                  <i className="fas fa-shield-halved me-2" style={{ color: EMPRESA_INFO.cor }}></i>
                  <strong>Privacidade:</strong> as transcrições e análises contêm informações de clientes — o uso é restrito
                  à gestão do atendimento e ao desenvolvimento do time, em linha com as diretrizes de privacidade da Comece Hub (LGPD).
                </div>
              </div>
            </div>
          </>
        ) : (
        <>
        {/* ── Pergunta central do tipo ── */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: `4px solid ${tipo.cor}` }}>
          <div className="card-body py-3 d-flex align-items-center gap-3 flex-wrap">
            <i className={`fas ${tipo.icone} fa-2x`} style={{ color: tipo.cor }}></i>
            <div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>{tipo.setores}</div>
              <strong style={{ fontSize: '1.05rem' }}>{tipo.pergunta}</strong>
            </div>
          </div>
        </div>

        {/* ── Competências ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white" style={{ borderBottom: `2px solid ${tipo.cor}` }}>
            <h6 className="mb-0">
              <i className="fas fa-bullseye me-2" style={{ color: tipo.cor }}></i>
              As {tipo.competencias.length} Competências Avaliadas
            </h6>
            <small className="text-muted">Cada uma recebe status (demonstrou / não demonstrou / não se aplica), nota 0–10 e uma evidência da transcrição</small>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {tipo.competencias.map((c, i) => (
                <div key={i} className="col-md-6">
                  <div className="d-flex gap-3 p-3 rounded-3 h-100" style={{ background: '#f8f9fa' }}>
                    <span className="badge rounded-pill flex-shrink-0" style={{
                      background: tipo.cor, width: 28, height: 28,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2,
                    }}>{i + 1}</span>
                    <div>
                      <strong style={{ fontSize: '0.95rem' }}>{c.nome}</strong>
                      <p className="mb-0 text-muted" style={{ fontSize: '0.87rem' }}>{c.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Boa vs Má entrega ── */}
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white" style={{ borderBottom: '2px solid #198754' }}>
                <h6 className="mb-0"><i className="fas fa-thumbs-up me-2 text-success"></i>O que é uma BOA entrega</h6>
              </div>
              <div className="card-body">
                {tipo.boa.map((item, i) => (
                  <div key={i} className="d-flex gap-2 mb-2">
                    <i className="fas fa-check-circle text-success flex-shrink-0" style={{ marginTop: 4 }}></i>
                    <span style={{ fontSize: '0.92rem' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white" style={{ borderBottom: '2px solid #dc3545' }}>
                <h6 className="mb-0"><i className="fas fa-thumbs-down me-2 text-danger"></i>O que é uma MÁ entrega</h6>
              </div>
              <div className="card-body">
                {tipo.ma.map((item, i) => (
                  <div key={i} className="d-flex gap-2 mb-2">
                    <i className="fas fa-times-circle text-danger flex-shrink-0" style={{ marginTop: 4 }}></i>
                    <span style={{ fontSize: '0.92rem' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Âncoras de nota ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white" style={{ borderBottom: `2px solid ${tipo.cor}` }}>
            <h6 className="mb-0">
              <i className="fas fa-ruler me-2" style={{ color: tipo.cor }}></i>
              Régua da Nota da Reunião (0–10)
            </h6>
            <small className="text-muted">Como a Memor.IA ancora a nota geral de cada reunião deste tipo</small>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {tipo.ancoras.map((a, i) => (
                <div key={i} className="col-md-6 col-lg-3">
                  <div className="p-3 rounded-3 h-100 text-center" style={{ background: `${a.cor}12`, border: `1px solid ${a.cor}40` }}>
                    <span className="badge mb-2" style={{ background: a.cor, fontSize: '0.95rem' }}>{a.faixa}</span>
                    <p className="mb-0" style={{ fontSize: '0.85rem' }}>{a.texto}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Regras que valem para todos os tipos ── */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white" style={{ borderBottom: '2px solid #00205b' }}>
            <h6 className="mb-0">
              <i className="fas fa-scale-balanced me-2" style={{ color: '#00205b' }}></i>
              Regras que Valem para Todos os Setores
            </h6>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {REGRAS_GERAIS.map((r, i) => (
                <div key={i} className="col-md-6 col-lg-4">
                  <div className="d-flex gap-3 p-3 rounded-3 h-100" style={{ background: '#f8f9fa' }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      background: '#e8eef7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className={`fas ${r.icon}`} style={{ color: '#00205b' }}></i>
                    </div>
                    <div>
                      <strong style={{ fontSize: '0.92rem' }}>{r.titulo}</strong>
                      <p className="mb-0 text-muted" style={{ fontSize: '0.84rem' }}>{r.texto}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Benefícios e funcionalidades do tipo selecionado ── */}
        {(tipo.beneficios || []).length > 0 && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white" style={{ borderBottom: `2px solid ${tipo.cor}` }}>
              <h6 className="mb-0">
                <i className="fas fa-gift me-2" style={{ color: tipo.cor }}></i>
                O que Você Ganha com as Reuniões de {tipo.label}
              </h6>
              <small className="text-muted">Benefícios e funcionalidades que a Memor.IA entrega a partir deste tipo de análise</small>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {tipo.beneficios.map((b, i) => (
                  <div key={i} className="col-md-6 col-lg-4">
                    <div className="d-flex gap-3 p-3 rounded-3 h-100" style={{ background: '#f8f9fa' }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                        background: `${tipo.cor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <i className={`fas ${b.icon}`} style={{ color: tipo.cor }}></i>
                      </div>
                      <div>
                        <strong style={{ fontSize: '0.92rem' }}>{b.titulo}</strong>
                        <p className="mb-0 text-muted" style={{ fontSize: '0.84rem' }}>{b.texto}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        </>
        )}

        {/* ── Encerramento ── */}
        <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #fdf2fa 0%, #fbe8f6 100%)', borderLeft: '4px solid #d610b5' }}>
          <div className="card-body py-4 text-center">
            <i className="fas fa-quote-left fa-2x mb-3" style={{ color: '#d610b5', opacity: 0.35 }}></i>
            <p className="mb-0" style={{ fontSize: '1.05rem', lineHeight: '1.7', maxWidth: 780, margin: '0 auto', color: '#4a3049' }}>
              A Memor.IA não existe para vigiar — existe para que <strong>nenhuma dor de cliente, nenhuma
              oportunidade de venda e nenhum operador que precisa de ajuda passem despercebidos</strong>.
              É tempo de gestão devolvido e desenvolvimento de time acelerado, todos os dias, em cada reunião.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
